import { twMerge } from 'tailwind-merge';

import { Icon, IconName } from '~/modules/elements/akui/icon';
import { PhoneTabs } from '~/routes/remote-mic/remote-mic';

const TABS: Array<{ tab: PhoneTabs; icon: IconName; label: string; test: string }> = [
  { tab: 'microphone', icon: 'ic:baseline-mic', label: 'Microphone', test: 'menu-microphone' },
  { tab: 'song-list', icon: 'ic:baseline-list', label: 'Song list', test: 'menu-song-list' },
  { tab: 'settings', icon: 'ic:baseline-settings', label: 'Settings', test: 'menu-settings' },
];

interface Props {
  // Which shape the bar takes. Driven by the shell's own orientation test rather than by a CSS
  // `landscape:` variant: the shell picks a different tree per orientation, and the two answering
  // the question differently put a vertical rail at the foot of the portrait layout.
  layout: 'bar' | 'rail';
  active: PhoneTabs;
  // Which tab the underline should already be under. A swipe past the commit threshold pre-lights
  // where it is heading: with no labels in the bar the underline is the only marker there is.
  heading?: PhoneTabs;
  setActiveTab: (active: PhoneTabs) => void;
  // While a song runs the phone is the microphone and nothing else, so the other two dim and a
  // press says why rather than doing nothing.
  lockedTabs?: PhoneTabs[];
  onLockedPress?: () => void;
  className?: string;
}

export default function BottomBar({
  layout,
  active,
  heading = active,
  setActiveTab,
  lockedTabs = [],
  onLockedPress,
  className,
}: Props) {
  const isRail = layout === 'rail';

  return (
    // Portrait: icons only, pinned to the bottom. Landscape: the vertical rail it has always been,
    // labels included - the redesign is portrait-only, and a rail has room for a word.
    <div
      className={twMerge(
        'flex shrink-0 items-stretch bg-black',
        isRail ? 'h-full w-24 flex-col justify-end' : 'h-17.5 w-full px-3 pb-5.5',
        className,
      )}>
      {TABS.map(({ tab, icon, label, test }) => {
        const locked = lockedTabs.includes(tab);
        const isActive = active === tab;

        return (
          <button
            key={tab}
            type="button"
            aria-label={label}
            aria-current={isActive}
            data-test={test}
            data-locked={locked || undefined}
            onClick={() => (locked ? onLockedPress?.() : setActiveTab(tab))}
            className={twMerge(
              'flex cursor-pointer flex-col items-center justify-center border-none bg-transparent transition-colors duration-200',
              isRail ? 'flex-none gap-2 py-4' : 'flex-1 gap-[0.3125rem]',
              isActive ? 'text-active' : locked ? 'text-white/22' : 'text-white/60',
            )}>
            <Icon icon={icon} size={7} />
            {isRail ? (
              <span className="text-xs">{label}</span>
            ) : (
              <span
                aria-hidden
                className={twMerge(
                  'h-[0.1875rem] rounded-full bg-current transition-[width,background-color] duration-200',
                  heading === tab ? 'w-5' : 'w-0',
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
